import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { CONSENT_VERSION, NOTICE_UPDATED } from '../../helpers/consent';

/**
 * `/privacy` — the notice text is shipped verbatim from
 * backlog/details/BL-0079/plan.md section 10; only the bracketed
 * substitutions below are computed. This is a route (config.addonRoutes),
 * not Plone content: no `privacy` object exists on any of the three sites,
 * so the backend content fetch 404s for this path and the router serves
 * this component directly (same pattern as FamilyTimeline's version-history
 * hub — a real page that is not a content object).
 *
 * RETENTION_ALB_DAYS gates the one sentence in "Server logs" whose truth
 * this frontend cannot itself confirm — whether the S3 lifecycle rule on the
 * shared ALB log bucket exists (plan section 8.2, user decision, unconfirmed
 * as of NOTICE_UPDATED). Only ever set this to 60 once that is verified;
 * shipping a placeholder or a guess here is exactly what section 10's "no
 * placeholder text" rule forbids.
 */
const RETENTION_ALB_DAYS = null;

const openChoices = (event) => {
  event.preventDefault();
  if (typeof window !== 'undefined' && window.ppConsent) {
    window.ppConsent.open();
  }
};

const COOKIES = [
  {
    name: 'pp_consent',
    setBy: 'us',
    purpose: 'remembers your privacy choices',
    lasts: '12 months',
    category: 'strictly necessary',
  },
  {
    name: 'pp_notice',
    setBy: 'us',
    purpose:
      'remembers that you have already been shown the privacy banner, so it is not repeated; it records nothing about what you chose',
    lasts: '12 months',
    category: 'strictly necessary',
  },
  {
    name: '_ga',
    setBy: 'Google Analytics',
    purpose: 'distinguishes your browser from others with a random identifier',
    lasts: 'up to 2 years',
    category: 'analytics (needs your choice)',
  },
  {
    name: '_ga_{id}',
    setBy: 'Google Analytics',
    purpose: 'keeps session state for this archive',
    lasts: 'up to 2 years',
    category: 'analytics (needs your choice)',
  },
  {
    name: 'pp_dev',
    setBy: 'us',
    purpose:
      'set only if a Doxcelerate staff member opens the archive with a diagnostic link; turns analytics off for that browser',
    lasts: '12 months',
    category: 'strictly necessary, staff use only',
  },
  {
    name: 'aws-waf-token',
    setBy: 'Amazon Web Services',
    purpose:
      'proves your browser is a real browser when you send the contact form, so the form cannot be used by automated scripts; it is set only on the contact page and holds no information about you',
    lasts: 'up to 24 hours',
    category: 'strictly necessary',
  },
  {
    name: 'auth_token',
    setBy: 'us',
    purpose: 'keeps an editor signed in',
    lasts: 'session',
    category: 'strictly necessary, staff use only',
  },
];

const PrivacyNotice = () => {
  const site = useSiteConfig();
  const origin = (site.baseUrl || '').replace(/\/+$/, '');
  const canonical = `${origin}/privacy`;
  const ga4Id = site.ga4 || '';
  const idSuffix = ga4Id.replace(/^G-/, '');
  const title = `Privacy notice — ${site.title}`;
  const description = `How ${site.title} handles server logs, Google Analytics, and cookies, and how to make and change your privacy choices.`;

  // id="view" is what Volto's "Skip to main content" skiplink targets. A
  // content route gets it from the default view wrapper; an addonRoutes page
  // renders its own root, so without this the skiplink is a dead link —
  // WCAG 2.4.1 — on the one page that has to be reachable.
  return (
    <div className="pp-privacy" id="view">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={site.title} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <div className="prc-kit__main">
        <article className="prc-kit__doc">
          <h1 className="prc-kit__doc-h1">Privacy notice</h1>
          <p>
            Last updated {NOTICE_UPDATED}. Consent version {CONSENT_VERSION}.
          </p>

          <h2>Summary</h2>
          <p>
            This archive is run by Doxcelerate Corporation. We collect very
            little: web server logs that every website keeps for security, and —
            with your permission where the law requires it — Google Analytics
            data about how the archive is used. We do not ask you to register.
            If you write to us through the contact form, we keep what you send
            until we have dealt with it. We do not sell your personal
            information for money. If you allow "Advertising" in your privacy
            choices, Google may link your visit to your Google account for
            advertising measurement. You can change your choices at any time
            from the "Do Not Sell or Share My Personal Information" link in the
            footer.
          </p>

          <h2>Who we are</h2>
          <p>
            Doxcelerate Corporation ("we", "us") operates this archive and is
            the controller of the personal data described here.
          </p>
          <ul>
            <li>
              Website:{' '}
              <a href="https://www.doxcelerate.com">
                https://www.doxcelerate.com
              </a>
            </li>
            <li>
              Privacy contact:{' '}
              <a href="mailto:doe.archives@doxcelerate.com">
                doe.archives@doxcelerate.com
              </a>
            </li>
          </ul>
          <p>
            We are located in the United States. This archive is not operated
            by, endorsed by, or affiliated with the U.S. Department of Energy or
            the National Nuclear Security Administration.
          </p>

          <h2>What this notice covers</h2>
          <p>This notice applies to the three Doxcelerate archives:</p>
          <ul>
            <li>
              <a href="https://archives.standards.doxcelerate.com">
                https://archives.standards.doxcelerate.com
              </a>{' '}
              (Technical Standards)
            </li>
            <li>
              <a href="https://archives.directives.doxcelerate.com">
                https://archives.directives.doxcelerate.com
              </a>{' '}
              (Directives)
            </li>
            <li>
              <a href="https://archives.nnsa.doxcelerate.com">
                https://archives.nnsa.doxcelerate.com
              </a>{' '}
              (NNSA Policies and Directives)
            </li>
          </ul>
          <p>
            They share one set of cookies (set for doxcelerate.com and its
            subdomains), so the privacy choice you make on one of them applies
            to all three. You are reading this notice on {site.title} (
            {new URL(origin || 'https://archives.doxcelerate.com').host}).
          </p>

          <h2>What we collect, why, and for how long</h2>

          <h3>Server logs</h3>
          <p>
            When your browser requests a page or a document, our web servers and
            our cloud provider's load balancer record the request: your IP
            address, the date and time, the address requested, the page that
            referred you, and your browser's identification string ("user
            agent").
          </p>
          <ul>
            <li>
              Purpose: keeping the archive secure and available, diagnosing
              faults, and understanding aggregate load (for example how many
              requests come from automated crawlers).
            </li>
            <li>
              Legal basis (EEA/UK/Swiss visitors): our legitimate interest in
              running a secure service (GDPR art. 6(1)(f)).
            </li>
            <li>
              Retention: web server logs are deleted after 60 days.{' '}
              {RETENTION_ALB_DAYS === 60 ? (
                <span>Load-balancer logs are deleted after 60 days.</span>
              ) : (
                <span>
                  We are putting a retention limit on the load-balancer logs;
                  until it is in place they are kept without a fixed limit, and
                  we will update this notice when that changes.
                </span>
              )}
            </li>
          </ul>

          <h3>Analytics (Google Analytics 4)</h3>
          <p>
            If analytics is on for you (see "Your choices"), we use Google
            Analytics 4, a service of Google LLC, to understand how the archive
            is used. Google Analytics sets cookies that give your browser a
            random identifier and sends Google, on our behalf:
          </p>
          <ul>
            <li>
              the pages you view and how you move between them, how far you
              scroll, and links you click to other sites;
            </li>
            <li>
              the search terms you type into the archive's search box, and how
              many results they returned;
            </li>
            <li>
              which documents you open or download (the file name and the
              document identifier);
            </li>
            <li>
              an approximate location (city or region) derived from your IP
              address at the moment of collection — Google Analytics does not
              store the IP address itself;
            </li>
            <li>
              your device type, browser, screen size, language, and the site
              that referred you.
            </li>
          </ul>
          <p>
            We use this to see which documents and searches matter to visitors,
            to find searches that return nothing, and to fix pages that fail. We
            do not use it to identify you personally, and we have not enabled
            Google's account-level data sharing with other Google products.
          </p>
          <ul>
            <li>
              Legal basis (EEA/UK/Swiss visitors): your consent (GDPR art.
              6(1)(a)), which you can withdraw at any time. Elsewhere, analytics
              is on by default and you can turn it off at any time.
            </li>
            <li>
              Retention: Google Analytics keeps event-level and user-level data
              for 14 months, after which it is deleted automatically. Aggregated
              reports do not identify you.
            </li>
            {ga4Id ? <li>Measurement ID for this archive: {ga4Id}.</li> : null}
          </ul>

          <h3>Advertising measurement (Google signals)</h3>
          <p>
            Only if you allow "Advertising" in your privacy choices, we enable
            Google signals. With it, Google associates your visits with your
            Google account if you are signed in and have "Ads Personalization"
            turned on in that account. This lets us see aggregated demographic
            and interest reports and, when we run Google Ads campaigns in the
            future, measure them and show relevant ads to people who visited the
            archive. Google, not we, holds the link to your account.
          </p>
          <ul>
            <li>
              Legal basis: your consent. Where you are in the United States,
              allowing Advertising means Google may use the data for
              cross-context behavioral advertising, which California law calls
              "sharing"; you can refuse or withdraw it with the footer link at
              any time.
            </li>
            <li>
              Ways to opt out beyond our banner: Google's Ads Settings at{' '}
              <a href="https://adssettings.google.com">
                adssettings.google.com
              </a>
              , the Google Analytics opt-out browser add-on at{' '}
              <a href="https://tools.google.com/dlpage/gaoptout">
                tools.google.com/dlpage/gaoptout
              </a>
              , and the Network Advertising Initiative at{' '}
              <a href="https://optout.networkadvertising.org">
                optout.networkadvertising.org
              </a>
              .
            </li>
            <li>
              How Google uses data from sites that use its services:{' '}
              <a href="https://policies.google.com/technologies/partner-sites">
                policies.google.com/technologies/partner-sites
              </a>
              . Google's privacy policy:{' '}
              <a href="https://policies.google.com/privacy">
                policies.google.com/privacy
              </a>
              .
            </li>
          </ul>

          <h3>Messages you send us</h3>
          <p>
            If you use the contact form at <Link to="/contact">/contact</Link>,
            we receive what you typed — your message, your email address, your
            name if you gave one, and any screenshots you attached — together
            with the context needed to act on it: which archive you were on, the
            page you came from, the document or search term the report is about,
            your browser's identification string, language, window size and time
            zone, and your IP address. The form links here so you can read this
            list before you send.
          </p>
          <ul>
            <li>
              Purpose: answering you, and correcting the archive. A report of a
              wrong date or a missing file is how most of this archive's
              corrections begin.
            </li>
            <li>
              Legal basis (EEA/UK/Swiss visitors): our legitimate interest in
              running and correcting the archive, and in replying to people who
              write to us (GDPR art. 6(1)(f)).
            </li>
            <li>
              Retention: we keep messages until they are dealt with and for as
              long afterwards as the record of the correction is useful, which
              may be indefinitely. Ask us and we will delete yours.
            </li>
            <li>
              We never send anything to an address typed into that form except a
              reply from a person. There is no automatic acknowledgement, no
              mailing list, and no way for a stranger to make the form send mail
              to somebody else.
            </li>
            <li>
              Screenshots are re-encoded when they arrive, which removes the
              camera and location data a phone photograph carries. Nothing you
              attach is published.
            </li>
            <li>
              We do not attach your analytics identifier, or anything else
              behind a privacy choice, to a message.
            </li>
          </ul>

          <h2>Cookies and similar technologies</h2>
          <p>
            Nothing is stored on your device until you make a choice, except the
            cookie that remembers the choice itself.
          </p>
          <table className="prc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Set by</th>
                <th>Purpose</th>
                <th>Lasts</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              {COOKIES.map((c) => (
                <tr key={c.name}>
                  <td>
                    <code>
                      {c.name === '_ga_{id}' && idSuffix
                        ? `_ga_${idSuffix}`
                        : c.name}
                    </code>
                  </td>
                  <td>{c.setBy}</td>
                  <td>{c.purpose}</td>
                  <td>{c.lasts}</td>
                  <td>{c.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            We do not use pixels, fingerprinting, or local storage for tracking.
          </p>

          <h2 id="your-choices">Your choices</h2>
          <ul>
            <li>
              <strong>The banner.</strong> The first time you visit, a bar at
              the bottom of the page offers "Accept all", "Decline", and
              "Manage" (separate switches for Analytics and Advertising).
              Declining is one click, the same as accepting.
            </li>
            <li>
              <strong>Changing your mind.</strong> Use "Do Not Sell or Share My
              Personal Information" in the footer of every page, or the button
              below. Withdrawing consent stops further collection immediately
              and deletes the Google Analytics cookies from your browser; it
              does not undo collection that already happened.
            </li>
            <li>
              <strong>Global Privacy Control.</strong> If your browser sends a
              Global Privacy Control signal, we treat it as an opt-out of
              advertising measurement and of any "sale" or "sharing" of your
              personal information, unless you later choose otherwise in the
              banner.
            </li>
            <li>
              <strong>Your browser.</strong> You can block or delete cookies in
              your browser settings; the archive works without cookies.
            </li>
            <li>
              If you are in the European Economic Area, the United Kingdom, or
              Switzerland, analytics and advertising are off until you turn them
              on. We ask again after 12 months.
            </li>
          </ul>
          <p>
            <button
              type="button"
              className="pp-consent__btn"
              onClick={openChoices}
            >
              Open privacy choices
            </button>
          </p>

          <h2>Who receives your data</h2>
          <ul>
            <li>
              <strong>Google LLC</strong>, 1600 Amphitheatre Parkway, Mountain
              View, CA 94043, USA — processes analytics data on our behalf under
              Google's Ads Data Processing Terms, and, if you allow Advertising,
              uses Google signals as described above.
            </li>
            <li>
              <strong>Amazon Web Services, Inc.</strong> — hosts the archive and
              its load balancer in the United States (region us-east-2).
            </li>
            <li>
              We do not sell personal information for money and we do not give
              it to data brokers. We disclose it only when the law requires (for
              example a valid legal order) or to protect the archive against
              abuse.
            </li>
          </ul>

          <h2>International transfers</h2>
          <p>
            We and our providers process data in the United States. If you visit
            from the European Economic Area, the United Kingdom, or Switzerland,
            your data is transferred to the United States when your browser
            connects to us. Google LLC and Amazon Web Services, Inc. are
            certified under the EU-U.S. Data Privacy Framework, its UK
            Extension, and the Swiss-U.S. Data Privacy Framework, and Google's
            Ads Data Processing Terms include the EU Standard Contractual
            Clauses as a fallback.
          </p>

          <h2>Your rights</h2>
          <p>Depending on where you live you may have the right to:</p>
          <ul>
            <li>
              know what personal data we hold about you and receive a copy;
            </li>
            <li>have it corrected or deleted;</li>
            <li>
              restrict or object to how we use it, including objecting to
              processing based on legitimate interests;
            </li>
            <li>receive it in a portable form;</li>
            <li>
              withdraw consent at any time, without affecting what was done
              before;
            </li>
            <li>
              opt out of the "sale" or "sharing" of your personal information
              and of targeted advertising (California and other U.S. states);
            </li>
            <li>
              limit the use of sensitive personal information (we do not collect
              any);
            </li>
            <li>not be discriminated against for exercising these rights;</li>
            <li>
              complain to a supervisory authority: your national data protection
              authority in the EEA, the Information Commissioner's Office in the
              UK (<a href="https://ico.org.uk">ico.org.uk</a>), the Federal Data
              Protection and Information Commissioner in Switzerland, or the
              California Privacy Protection Agency (
              <a href="https://cppa.ca.gov">cppa.ca.gov</a>).
            </li>
          </ul>
          <p>
            To exercise a right, email{' '}
            <a href="mailto:doe.archives@doxcelerate.com">
              doe.archives@doxcelerate.com
            </a>
            , or use the form at <Link to="/contact">/contact</Link> and choose
            "Something else". Either way, tell us the archive you visited and
            what you are asking. Because we hold no account for you, we may ask
            for enough information to match your request to our records (for
            example the date and approximate time of your visits); a request we
            cannot match, we cannot fulfil. We respond within one month
            (EEA/UK/Switzerland) or 45 days (California), and we do not charge a
            fee. You may use an authorised agent; we will ask the agent for
            proof of your authorisation.
          </p>

          <h2>Children</h2>
          <p>
            The archive is a reference collection for professionals and is not
            directed to children. We do not knowingly collect personal data from
            anyone under 16 (under 13 in the United States). If you believe a
            child has provided us data, email us and we will delete it.
          </p>

          <h2>Security</h2>
          <p>
            Every page is served over HTTPS. Access to servers and to analytics
            accounts is limited to staff who need it and protected with strong
            authentication.
          </p>

          <h2>Automated decision-making</h2>
          <p>
            We make no decisions about you by automated means and we do not
            profile you.
          </p>

          <h2>Changes to this notice</h2>
          <p>
            We will post changes here with a new "Last updated" date. If a
            change adds a new purpose for which we need your consent, the banner
            will ask you again. Earlier versions are kept in our source-control
            history.
          </p>

          <h2>Contact</h2>
          <p>
            Use the form at <Link to="/contact">/contact</Link>, or email{' '}
            <a href="mailto:doe.archives@doxcelerate.com">
              doe.archives@doxcelerate.com
            </a>
            . Both reach the same people. Doxcelerate Corporation,{' '}
            <a href="https://www.doxcelerate.com">
              https://www.doxcelerate.com
            </a>
          </p>
        </article>
      </div>
    </div>
  );
};

export default PrivacyNotice;
